<template>
    <div class="padding-normal">
        <div class="normal-container">
            <NuxtImg
                v-if="image"
                :src="useImageUrl(image).format('webp').url()"
                class="h-6 w-6 lg:h-[50px] lg:w-[50px]"
                height="50px"
                width="50px"
                :alt="image?.alt ?? 'instagram'"
                loading="lazy" />
            <h2 class="my-2 text-lg lg:my-4 lg:text-3xl">
                {{ title }}
            </h2>
            <p>{{ text }}</p>
        </div>

        <div class="mt-5 w-full overflow-hidden">
            <div class="normal-case normal-container">
                <LazyUiSlider
                    ref="slider"
                    :breakpoints="{
                        '(min-width: 0px)': {
                            slides: { perView: 'auto', spacing: 16 },
                        },
                    }">
                    <template v-if="instagramData && instagramData.length > 0">
                        <div
                            v-for="(item, index) in instagramData"
                            :key="item.id"
                            :class="['keen-slider__slide number-slide' + index]"
                            class="rounded-sm min-w-[128px] max-w-[128px] lg:min-w-[230px] lg:max-w-[230px]">
                            <div class="group relative h-40 w-32 lg:h-[280px] lg:w-[230px]">
                                <NuxtLink :to="item.permalink" target="_blank">
                                    <NuxtImg
                                        :src="item.image_url"
                                        class="h-full w-full object-cover duration-300"
                                        format="webp"
                                        alt="Instagram image"
                                        loading="lazy" />
                                </NuxtLink>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div
                            v-for="n in 6"
                            :key="n"
                            :class="['keen-slider__slide number-slide' + (n - 1)]"
                            class="min-w-[128px] max-w-[128px] animate-pulse overflow-hidden lg:min-w-[230px] lg:max-w-[230px]">
                            <div class="group relative h-40 w-32 bg-gray-200 lg:h-[280px] lg:w-[230px]" />
                        </div>
                    </template>
                </LazyUiSlider>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
    import { useKeenSlider } from 'keen-slider/vue';
    import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/vue/24/outline';

    const INSTA_FEED_WOMAN_ID = '2';
    const INSTA_FEED_MAN_ID = '3';

    const slider = useTemplateRef('slider');

    interface InstagramResponse {
        count: number;
        items: InstagramPost[];
    }

    interface InstagramPost {
        id: string;
        permalink: string;
        image_url: string;
        full_res_url: string;
    }

    const props = defineProps<{
        gender: 'men' | 'women';
        image: any;
        text: string;
        title: string;
    }>();

    const instagramData: Ref<InstagramPost[] | null> = ref(null);

    watch(
        instagramData,
        async newData => {
            if (newData && newData.length > 0 && slider.value) {
                await nextTick();
                // Wait a bit more to ensure DOM is fully rendered
                setTimeout(() => {
                    if (slider.value) {
                        slider.value.update();
                    }
                }, 300);
            }
        },
        { immediate: false },
    );

    onMounted(() => {
        const feedId = props.gender === 'men' ? INSTA_FEED_MAN_ID : INSTA_FEED_WOMAN_ID;
        fetch(`https://instagram.dotsdev.nl/wp-json/v1/instagram_feed?feed_id=${feedId}&limit=12`)
            .then(response => {
                if (!response.ok) throw new Error('Failed to fetch Instagram feed');
                return response.json();
            })
            .then((data: InstagramResponse) => {
                instagramData.value = data.items;
            })
            .catch(error => {
                console.error('Error fetching Instagram feed:', error);
                return null;
            });
    });
</script>
